/**
 * <Textarea> — multi-line text input. Same Field-context behavior as Input.
 *
 * Resolves the 0% Textarea-primitive adoption from Phase-1 finding C5
 * (every textarea today is a raw <textarea>).
 *
 * Spec: docs/design-system/06-buttons-forms.md.
 */

import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { useFieldContext, describedByFromContext } from '../Field/FieldContext';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Force invalid state when used standalone. */
  error?: boolean;
  /** Show as full width of parent. Default `true`. */
  fullWidth?: boolean;
  /** Resize behavior. Default `vertical`. */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

const RESIZE_CLASSES: Record<NonNullable<TextareaProps['resize']>, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    error: errorProp,
    fullWidth = true,
    resize = 'vertical',
    rows = 3,
    className,
    id: idProp,
    'aria-describedby': describedByProp,
    'aria-invalid': invalidProp,
    'aria-required': requiredProp,
    'aria-disabled': _ariaDisabledProp,
    disabled: disabledProp,
    required: requiredAttr,
    ...rest
  },
  ref,
) {
  const ctx = useFieldContext();

  const id = ctx?.inputId ?? idProp;
  const describedBy = describedByFromContext(ctx, describedByProp);
  const invalid = ctx?.invalid ?? errorProp ?? false;
  const required = ctx?.required ?? requiredAttr ?? false;
  // Most-restrictive-wins: Field always forwards a `disabled` value into
  // context (default `false` when the caller never set one on <Field>
  // itself), so `??` alone would let that default short-circuit a truthy
  // `disabled` passed directly to this control. OR-ing the two means either
  // side setting `disabled` disables the rendered control, matching the
  // "most restrictive wins" contract instead of silently favoring context.
  const disabled = Boolean(ctx?.disabled) || Boolean(disabledProp);

  return (
    <textarea
      ref={ref}
      id={id}
      rows={rows}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      // Redundant with the native `disabled` attribute below (which already
      // implies this to assistive tech) — kept explicit so AT and any
      // `[aria-disabled]` styling hook see the state directly. Intentional.
      aria-disabled={disabled || undefined}
      disabled={disabled}
      required={required}
      className={cn(
        'px-3 py-2 text-sm bg-surface border rounded-md text-text placeholder:text-subtle',
        'focus:outline-none focus:ring-2 transition-colors',
        'disabled:bg-bg disabled:opacity-60 disabled:cursor-not-allowed',
        invalid
          ? 'border-danger focus:ring-danger/20 focus:border-danger'
          : 'border-border focus:ring-primary/20 focus:border-primary',
        RESIZE_CLASSES[resize],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    />
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;
